import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, withModifiers as _withModifiers, withKeys as _withKeys, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, withCtx as _withCtx, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "outage-select-container" }
const _hoisted_2 = ["innerHTML"]
const _hoisted_3 = {
  key: 1,
  class: "outage-dropdown"
}

import {ref, watch} from 'vue'
import {supabase} from '@/lib/supabase'
import {useGlobalSetting} from '@/lib/utils'
import {StateColor} from '@/types/Order'

interface OutageItem {
  id: number
  title: string
  subtitle: string
  state: string
}

const PAGE_SIZE = 30

export default /*@__PURE__*/_defineComponent({
  __name: 'OutageSelect',
  props: /*@__PURE__*/_mergeModels({
  label: {type: String, default: 'Auftrag wählen (optional)'},
  prependInnerIcon: {type: String, default: 'mdi-clipboard-text-outline'},
  clearable: {type: Boolean, default: true},
  rounded: {type: String, default: ''},
}, {
    "modelValue": {},
    "modelModifiers": {},
  }),
  emits: ["update:modelValue"],
  setup(__props) {

const model = _useModel<number | null>(__props, "modelValue")

const props = __props

const searchText = ref('')
const items = ref<OutageItem[]>([])
const loading = ref(false)
const showDropdown = ref(false)
const highlightedIndex = ref(-1)
const totalItems = ref(0)
const currentOffset = ref(0)
let debounceTimer: ReturnType<typeof setTimeout> | null = null

// --- Data Loading ---

async function loadPage(offset: number, search: string) {
  if (loading.value) return
  loading.value = true

  try {
    const {data, error} = await supabase.rpc('filter_rows', {
      p_table: 'outages',
      p_filters: {},
      p_sort_column: 'code',
      p_sort_order: 'desc',
      p_limit: PAGE_SIZE,
      p_offset: offset,
      p_select: 'code, name, description',
      p_search: search?.trim() || '',
      p_search_columns: ['code', 'name', 'description']
    })

    if (error) {
      console.error('Outage search failed:', error)
      return
    }

    const rows = data?.items || []
    const total = data?.total || 0

    const mapped = rows.map((o: any) => ({
      id: o.code,
      title: o.name || 'Ohne Titel',
      subtitle: o.description,
    }))

    if (offset === 0) {
      items.value = mapped
    } else {
      items.value.push(...mapped)
    }

    currentOffset.value = offset + mapped.length
    totalItems.value = total
  } finally {
    loading.value = false
  }
}

// --- Event Handlers ---

function onFocus() {
  showDropdown.value = true
  highlightedIndex.value = -1
  if (items.value.length === 0) {
    loadPage(0, searchText.value || '')
  }
}

function onBlur() {
  setTimeout(() => {
    showDropdown.value = false
    highlightedIndex.value = -1
  }, 150)
}

function onInput() {
  highlightedIndex.value = -1
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => {
    currentOffset.value = 0
    loadPage(0, searchText.value || '')
  }, 300)

  // Wenn der User tippt, ist die Auswahl ungültig
  model.value = null
}

function onClear() {
  model.value = null
  searchText.value = ''
  currentOffset.value = 0
  loadPage(0, '')
}

function onScroll(event: Event) {
  const el = event.target as HTMLElement
  const isNearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 50

  if (isNearBottom && currentOffset.value < totalItems.value && !loading.value) {
    loadPage(currentOffset.value, searchText.value || '')
  }
}

function selectOutage(item: OutageItem) {
  model.value = item.id
  searchText.value = `${item.id} - ${item.title}`
  showDropdown.value = false
  highlightedIndex.value = -1
}

function navigateDown() {
  if (highlightedIndex.value < items.value.length - 1) {
    highlightedIndex.value++
  }
}

function navigateUp() {
  if (highlightedIndex.value > 0) {
    highlightedIndex.value--
  }
}

function selectHighlighted() {
  if (highlightedIndex.value >= 0 && highlightedIndex.value < items.value.length) {
    selectOutage(items.value[highlightedIndex.value])
  }
}

function closeDropdown() {
  showDropdown.value = false
  highlightedIndex.value = -1
}

// --- Highlight Helper ---

function highlight(text: string, search: string | null): string {
  if (!search || !text) return text
  const escaped = search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const regex = new RegExp(`(${escaped})`, 'gi')
  return text.replace(regex, '<mark class="search-highlight">$1</mark>')
}

// --- Wenn model von außen gesetzt wird, lade den Outage ---

watch(model, async (newVal) => {
  if (newVal && !searchText.value.includes(`${newVal}`)) {
    const {data} = await supabase
        .from('outages')
        .select('id, title')
        .eq('id', newVal)
        .single()

    if (data) {
      searchText.value = `${data.id} - ${data.title || 'Ohne Titel'}`
    }
  }
}, {immediate: true})

// Initial laden
loadPage(0, '')

return (_ctx: any,_cache: any) => {
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_avatar = _resolveComponent("v-avatar")!
  const _component_v_list_item_title = _resolveComponent("v-list-item-title")!
  const _component_v_list_item_subtitle = _resolveComponent("v-list-item-subtitle")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_list_item = _resolveComponent("v-list-item")!
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!
  const _component_v_list = _resolveComponent("v-list")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_v_text_field, {
      modelValue: searchText.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchText).value = $event)),
      label: __props.label,
      variant: _unref(useGlobalSetting)().globalTextVariant,
      "prepend-inner-icon": __props.prependInnerIcon,
      clearable: __props.clearable,
      rounded: __props.rounded,
      "hide-details": "",
      onFocus: onFocus,
      onBlur: onBlur,
      onInput: onInput,
      "onClick:clear": onClear,
      onKeydown: [
        _withKeys(_withModifiers(navigateDown, ["prevent"]), ["arrow-down"]),
        _withKeys(_withModifiers(navigateUp, ["prevent"]), ["arrow-up"]),
        _withKeys(_withModifiers(selectHighlighted, ["prevent"]), ["enter"]),
        _withKeys(closeDropdown, ["escape"])
      ]
    }, null, 8, ["modelValue", "label", "variant", "prepend-inner-icon", "clearable", "rounded", "onKeydown"]),
    (showDropdown.value && items.value.length > 0)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "outage-dropdown",
          onScroll: onScroll
        }, [
          _createVNode(_component_v_list, {
            density: "compact",
            class: "outage-list"
          }, {
            default: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(items.value, (item, index) => {
                return (_openBlock(), _createBlock(_component_v_list_item, {
                  key: item.id,
                  class: _normalizeClass({ 'item-highlighted': highlightedIndex.value === index }),
                  onMousedown: _withModifiers(($event: any) => (selectOutage(item)), ["prevent"]),
                  onMouseenter: ($event: any) => (highlightedIndex.value = index)
                }, {
                  prepend: _withCtx(() => [
                    _createVNode(_component_v_avatar, {
                      size: "32",
                      color: "grey-lighten-3"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_icon, {
                          icon: "mdi-clipboard-text-outline",
                          size: "18"
                        })
                      ]),
                      _: 1
                    })
                  ]),
                  append: _withCtx(() => [
                    (item.state)
                      ? (_openBlock(), _createBlock(_component_v_chip, {
                          key: 0,
                          color: _unref(StateColor)[item.state],
                          size: "x-small",
                          variant: "flat"
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(item.state), 1)
                          ]),
                          _: 2
                        }, 1032, ["color"]))
                      : _createCommentVNode("", true)
                  ]),
                  default: _withCtx(() => [
                    _createVNode(_component_v_list_item_title, { class: "font-weight-medium" }, {
                      default: _withCtx(() => [
                        _createElementVNode("span", {
                          innerHTML: highlight(`${item.id} - ${item.title}`, searchText.value)
                        }, null, 8, _hoisted_2)
                      ]),
                      _: 2
                    }, 1024),
                    (item.subtitle)
                      ? (_openBlock(), _createBlock(_component_v_list_item_subtitle, { key: 0 }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(item.subtitle), 1)
                          ]),
                          _: 2
                        }, 1024))
                      : _createCommentVNode("", true)
                  ]),
                  _: 2
                }, 1032, ["class", "onMousedown", "onMouseenter"]))
              }), 128)),
              (loading.value)
                ? (_openBlock(), _createBlock(_component_v_list_item, {
                    key: 0,
                    class: "d-flex justify-center"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_progress_circular, {
                        indeterminate: "",
                        size: "20",
                        width: "2"
                      })
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true)
            ]),
            _: 1
          })
        ], 32))
      : _createCommentVNode("", true),
    (showDropdown.value && items.value.length === 0 && searchText.value && !loading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createVNode(_component_v_list, { density: "compact" }, {
            default: _withCtx(() => [
              _createVNode(_component_v_list_item, null, {
                prepend: _withCtx(() => [
                  _createVNode(_component_v_icon, { color: "grey" }, {
                    default: _withCtx(() => _cache[1] || (_cache[1] = [
                      _createTextVNode("mdi-magnify-close")
                    ])),
                    _: 1,
                    __: [1]
                  })
                ]),
                default: _withCtx(() => [
                  _createVNode(_component_v_list_item_title, { class: "text-grey" }, {
                    default: _withCtx(() => _cache[2] || (_cache[2] = [
                      _createTextVNode(" Keine Aufträge gefunden ")
                    ])),
                    _: 1,
                    __: [2]
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})